import { useMemo, useState } from "react";
import {
  describePlacedBetPhase,
  placedBetProgress,
} from "../settlement-state.js";
import {
  clockLabel,
  fractionElapsed,
  projectedFinal,
  projectionBeatsLine,
  roomToLine,
} from "../game-clock.js";
import { AmendBetDialog } from "../components/amend-bet-dialog.js";
import { Figure, KpiStrip } from "../components/tile.js";
import {
  betCaption,
  ChartControls,
  resolvedAt,
  UnitsChart,
  useChartState,
  type UnitsPoint,
} from "../components/units-chart.js";
import type { LedgerRowView } from "../api/client.js";
import type { PrototypeLedgerRow } from "../prototype/types.js";
import { melbourneClock, melbourneDay, melbourneStamp } from "../melbourne.js";
import { TableToolbar } from "../components/table-toolbar.js";

type LedgerSort =
  "newest" | "oldest" | "match" | "book" | "stake" | "price" | "profit";
const LEDGER_SORTS: readonly {
  readonly id: LedgerSort;
  readonly label: string;
}[] = [
  { id: "newest", label: "Newest bet" },
  { id: "oldest", label: "Oldest bet" },
  { id: "match", label: "Match time" },
  { id: "book", label: "Book" },
  { id: "stake", label: "Stake, largest" },
  { id: "price", label: "Price, highest" },
  { id: "profit", label: "Profit, best" },
];

type RawLedgerRow = Record<string, unknown> | PrototypeLedgerRow;

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}
function pretipStatus(
  value: unknown,
): "EXACT" | "LINE_MOVED" | "UNAVAILABLE" | null {
  return value === "EXACT" || value === "LINE_MOVED" || value === "UNAVAILABLE"
    ? value
    : null;
}
function numberOrNull(value: unknown): number | null {
  const parsed = Number(value);
  return value === null || value === undefined || !Number.isFinite(parsed)
    ? null
    : parsed;
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
    ? String(value)
    : fallback;
}

/**
 * A value fit to put in front of the operator, or the fallback.
 *
 * Internal ids are namespaced with colons - "rapidodds:event:998f6d3a...",
 * "nfl:team:cleveland-browns" - and several ledger fields fall back through
 * to one when a readable name is missing. The ledger showed an NFL bet as
 * "rapidodds:event:998f6d3a34955568692cc636 OVER NFL_GAME_TOTAL".
 *
 * The API resolves those names now; this is the backstop, so a sport wired up
 * later cannot leak one again. Ids stay in the data and in the API response -
 * they are needed for links and support - they just never get rendered.
 */
function readable(value: unknown, fallback = ""): string {
  const text = stringValue(value, fallback);
  return text.includes(":") ? fallback : text;
}

function resultValue(value: unknown): LedgerRowView["result"] {
  return value === "WIN" ||
    value === "LOSS" ||
    value === "PUSH" ||
    value === "VOID"
    ? value
    : null;
}

function statusValue(value: unknown): LedgerRowView["status"] {
  return value === "SETTLED" || value === "VOID" ? value : "PLACED";
}

/**
 * "T6" or "B9", the way a scoreboard writes it.
 *
 * Only baseball has innings, so the other sports pass null and the reading is
 * left out rather than rendered as an em dash - an empty slot in a live panel
 * reads as missing data, which is worse than not offering it.
 */
function inningLabel(
  progress: NonNullable<LedgerRowView["liveProgress"]> | null,
): string | null {
  if (!progress || progress.inningsPlayed === null) return null;
  const half = progress.inningHalf === "TOP" ? "T" : "B";
  return `${half}${progress.inningsPlayed}`;
}

/**
 * Where the player sits in the game: out of it, up now, or how many hitters
 * away. Null for anything but a baseball player bet with a lineup reading.
 */
function lineupLabel(
  progress: NonNullable<LedgerRowView["liveProgress"]> | null,
): string | null {
  if (!progress) return null;
  if (progress.playerRemoved === true) return "Out of game";
  const slot = progress.battingSlot ?? null;
  const away = progress.battersAway ?? null;
  if (slot === null || away === null) return null;
  const suffix =
    slot === 1 ? "st" : slot === 2 ? "nd" : slot === 3 ? "rd" : "th";
  return away === 0
    ? `${String(slot)}${suffix} · ${progress.teamBatting ? "up now" : "leads off"}`
    : `${String(slot)}${suffix} · ${String(away)} away`;
}

function liveProgressValue(
  value: unknown,
): NonNullable<LedgerRowView["liveProgress"]> | null {
  const progress = record(value);
  const status = progress.game_status;
  if (status !== "IN_PROGRESS" && status !== "FINAL") return null;
  return {
    status,
    actualValue: numberOrNull(progress.actual_value),
    homeScore: numberOrNull(progress.home_score),
    awayScore: numberOrNull(progress.away_score),
    opportunityValue: numberOrNull(progress.opportunity_value),
    opportunityLabel:
      typeof progress.opportunity_label === "string"
        ? progress.opportunity_label
        : null,
    period: numberOrNull(progress.period),
    clockSeconds: numberOrNull(progress.clock_seconds),
    inningsPlayed: numberOrNull(progress.innings_played),
    inningHalf:
      progress.inning_half === "TOP" || progress.inning_half === "BOTTOM"
        ? progress.inning_half
        : null,
    outs: numberOrNull(progress.outs),
    battingSlot: numberOrNull(progress.batting_slot),
    battersAway: numberOrNull(progress.batters_away),
    teamBatting:
      typeof progress.team_batting === "boolean" ? progress.team_batting : null,
    playerRemoved: progress.player_removed === true,
    expectedResult:
      progress.expected_result === "WIN" || progress.expected_result === "LOSS"
        ? progress.expected_result
        : null,
    observedAt: stringValue(progress.observed_at),
  };
}

/**
 * MLB, NHL and NFL are served straight out of their own
 * `*_recommendations` tables, which share none of the NBA `bets` shape: no
 * nested `signals`/`bet_games`/`settlements`, and different column names for
 * the rule, the side and the stake. Mapping them through the NBA reader
 * rendered every row as "NBA game · H2H @ 0.00 · 0u · A$0.00 · Awaiting
 * settlement" - even for a bet that had already settled WIN.
 */
function isRecommendationRow(row: Record<string, unknown>): boolean {
  return row.signals === undefined && typeof row.state === "string";
}

function normalizeRecommendation(
  row: Record<string, unknown>,
  index: number,
): LedgerRowView {
  const market = stringValue(row.market);
  // NFL calls it `selection`; MLB and NHL call it `side`.
  const selection = stringValue(row.selection ?? row.side);
  // MLB carries a readable name; NHL keys players, NFL keys subjects.
  const subject = readable(row.subject_name ?? row.subject_id ?? row.player_id);
  const units = Number(row.intended_units ?? 0);
  const stakeCents = Number(
    row.actual_stake_cents ?? row.intended_stake_cents ?? 0,
  );
  // These tables record profit in cents, not units. Recover the unit size
  // from the stake so the chart and the row agree with the NBA rows.
  const unitCents = units > 0 ? stakeCents / units : 0;
  const profitCents = numberOrNull(row.profit_cents);
  const state = stringValue(row.state);
  return {
    betId: stringValue(row.id, `ledger-${index}`),
    signalId: stringValue(row.natural_key),
    placedAt: stringValue(row.placed_at ?? row.created_at),
    settledAt: textOrNull(row.settled_at),
    ruleVersion: stringValue(
      row.rule_name ?? row.controlling_rule_id ?? row.rule_version,
    ),
    // The API words it (bet-description.ts); the keys are a last resort.
    description:
      textOrNull(row.description) ??
      [subject, selection, market].filter(Boolean).join(" "),
    selection,
    gameLabel: readable(row.subject_name ?? row.game_id, "Game"),
    matchup: textOrNull(row.matchup),
    bookmaker: stringValue(row.bookmaker),
    actualLine: numberOrNull(row.placed_line ?? row.line),
    actualPrice: Number(row.placed_price ?? row.offered_odds ?? 0),
    stakeCents,
    executionMode:
      row.execution_mode === "TEST_ONLY" || row.execution_mode === "TRACK_ONLY"
        ? row.execution_mode
        : "LIVE",
    gameStartsAt: textOrNull(row.game_datetime),
    settlementSource: textOrNull(row.settlement_source),
    alertStatus: textOrNull(row.alert_status),
    loggedAfterTheFact: row.logged_after_the_fact === true,
    units,
    status: state === "SETTLED" || state === "VOID" ? state : "PLACED",
    result: resultValue(row.result),
    actualValue: numberOrNull(row.actual_value),
    resultReason: textOrNull(row.result_reason),
    profitUnits:
      profitCents === null || unitCents <= 0 ? null : profitCents / unitCents,
    closingLine: numberOrNull(row.close_line),
    closingPrice: numberOrNull(row.close_price),
    lineClv: numberOrNull(row.line_clv),
    priceClv: numberOrNull(row.price_clv),
    lastPretipLine: numberOrNull(row.last_pretip_line),
    lastPretipPrice: numberOrNull(row.last_pretip_price),
    lastPretipSnapshotAt: textOrNull(row.last_pretip_snapshot_at),
    lastPretipSourceAt: textOrNull(row.last_pretip_source_at),
    pretipLineMovement: numberOrNull(row.pretip_line_movement),
    pretipPriceMovement: numberOrNull(row.pretip_price_movement),
    pretipSnapshotStatus: pretipStatus(row.pretip_snapshot_status),
    fallbackSnapshotLine: numberOrNull(row.fallback_snapshot_line),
    fallbackSnapshotPrice: numberOrNull(row.fallback_snapshot_price),
    fallbackSnapshotWindow:
      row.fallback_snapshot_window === null ||
      row.fallback_snapshot_window === undefined
        ? null
        : stringValue(row.fallback_snapshot_window),
    fallbackSnapshotObservedAt:
      row.fallback_snapshot_observed_at === null ||
      row.fallback_snapshot_observed_at === undefined
        ? null
        : stringValue(row.fallback_snapshot_observed_at),
    synthetic: false,
    sport: stringValue(row.sport, "NFL"),
    liveProgress: liveProgressValue(row.live_progress),
  };
}

export function normalizeLiveLedger(
  rows: readonly RawLedgerRow[],
): readonly LedgerRowView[] {
  return rows.map((raw, index) => {
    const row = record(raw);
    if (row.synthetic === true) return raw as PrototypeLedgerRow;
    if (isRecommendationRow(row)) return normalizeRecommendation(row, index);
    const signal = record(row.signals);
    const game = record(signal.bet_games);
    // settlements.bet_id is unique, so PostgREST embeds one object, not a
    // list: reading only arrays left every NBA bet "open" (24 Oct 2026).
    const settlement = record(
      Array.isArray(row.settlements) ? row.settlements[0] : row.settlements,
    );
    const market = stringValue(signal.market);
    const selection = stringValue(signal.selection);
    const subject = stringValue(signal.subject_id);
    const status = statusValue(row.status);
    return {
      betId: stringValue(row.id, `ledger-${index}`),
      signalId: stringValue(row.signal_id),
      placedAt: stringValue(row.placed_at),
      settledAt: textOrNull(settlement.settled_at ?? row.settled_at),
      ruleVersion: stringValue(row.rule_name ?? signal.rule_version),
      description:
        textOrNull(row.description) ??
        [subject, selection, market].filter(Boolean).join(" "),
      selection,
      gameLabel: stringValue(game.game_label, "NBA game"),
      bookmaker: stringValue(row.bookmaker),
      // NBA bets store `line` and `price`; the card read "H2H @ 0.00".
      actualLine: numberOrNull(row.actual_line ?? row.offered_line ?? row.line),
      actualPrice: Number(
        row.actual_price ?? row.offered_price ?? row.price ?? 0,
      ),
      matchup: textOrNull(row.matchup),
      stakeCents: Number(row.stake_cents ?? 0),
      executionMode:
        row.execution_mode === "TEST_ONLY" ||
        row.execution_mode === "TRACK_ONLY"
          ? row.execution_mode
          : "LIVE",
      gameStartsAt: textOrNull(row.game_datetime ?? row.tip_utc),
      settlementSource: textOrNull(row.settlement_source),
      alertStatus: textOrNull(row.alert_status),
      loggedAfterTheFact: row.logged_after_the_fact === true,
      units: Number(row.units ?? 0),
      status,
      result: resultValue(settlement.result ?? row.result),
      actualValue: numberOrNull(row.actual_value ?? settlement.actual_value),
      resultReason: textOrNull(row.result_reason),
      liveNote: textOrNull(row.live_note),
      profitUnits: numberOrNull(settlement.profit_units ?? row.profit_units),
      closingLine: numberOrNull(settlement.closing_line ?? row.closing_line),
      closingPrice: numberOrNull(settlement.closing_price ?? row.closing_price),
      lineClv: numberOrNull(settlement.line_clv ?? row.line_clv),
      priceClv: numberOrNull(settlement.price_clv ?? row.price_clv),
      lastPretipLine: numberOrNull(row.last_pretip_line),
      lastPretipPrice: numberOrNull(row.last_pretip_price),
      lastPretipSnapshotAt: textOrNull(row.last_pretip_snapshot_at),
      lastPretipSourceAt: textOrNull(row.last_pretip_source_at),
      pretipLineMovement: numberOrNull(row.pretip_line_movement),
      pretipPriceMovement: numberOrNull(row.pretip_price_movement),
      pretipSnapshotStatus: pretipStatus(row.pretip_snapshot_status),
      fallbackSnapshotLine: null,
      fallbackSnapshotPrice: null,
      fallbackSnapshotWindow: null,
      fallbackSnapshotObservedAt: null,
      synthetic: false,
      sport: stringValue(row.sport, "NFL"),
      liveProgress: liveProgressValue(row.live_progress),
    };
  });
}

const money = (cents: number) => `A$${(cents / 100).toFixed(2)}`;
const decimal = (value: number | null) =>
  value === null ? "—" : Number(value.toFixed(2)).toString();
const signedFixed = (value: number, places: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(places)}`;
const sentenceCase = (value: string | undefined): string =>
  value
    ? `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`
    : "";

const OUTCOMES: readonly {
  readonly id: "ALL" | "WIN" | "LOSS" | "PUSH" | "VOID" | "OPEN";
  readonly label: string;
  readonly tone?: "positive" | "warning" | "negative";
}[] = [
  { id: "ALL", label: "All" },
  { id: "WIN", label: "Won", tone: "positive" },
  { id: "LOSS", label: "Lost", tone: "negative" },
  { id: "PUSH", label: "Push" },
  { id: "VOID", label: "Void", tone: "warning" },
  { id: "OPEN", label: "Open", tone: "warning" },
];

function countFor(
  rows: readonly { readonly result: string | null }[],
  id: (typeof OUTCOMES)[number]["id"],
): number {
  if (id === "ALL") return rows.length;
  if (id === "OPEN") return rows.filter((row) => row.result === null).length;
  return rows.filter((row) => row.result === id).length;
}

/**
 * The bankroll curve, bet by bet.
 *
 * Only bets that moved money. Tracked rules stake nothing, so each one is a
 * flat step that cannot change the curve - and when the fix letting bench
 * rules record a result landed on 11 September, a fortnight's backlog graded
 * inside sixty seconds and drew nineteen consecutive flat points. That read as
 * broken settlement and took an hour to disprove. Tracked rules are judged on
 * strike rate and closing line value, never on units.
 *
 * Ordering is by settlement, and that is deliberate: settlement is when the
 * bankroll actually moved. Ordering by placement showed a Tuesday win on
 * Monday night, and ordering by game time would have the same fault.
 */
export function cumulativeUnits(
  rows: readonly LedgerRowView[],
): readonly UnitsPoint[] {
  return [...rows]
    .filter((row) => row.executionMode !== "TEST_ONLY" && row.stakeCents > 0)
    .sort((left, right) => resolvedAt(left).localeCompare(resolvedAt(right)))
    .reduce<UnitsPoint[]>((points, row) => {
      const previous = points.at(-1)?.units ?? 0;
      // A full ISO date, not MM-DD: the chart parses this, and a bare MM-DD
      // parses as a date in 2001, which quietly made Monthly identical to
      // Daily and anchored Weekly to the wrong year's weekdays.
      points.push({
        day: melbourneDay(resolvedAt(row)),
        units: previous + (row.profitUnits ?? 0),
        caption: betCaption(row),
      });
      return points;
    }, []);
}

/**
 * Tracked rules: recorded, graded and closed for CLV, but never official.
 * Judged on strike rate and closing-line value; they stake nothing, so a
 * units column would only ever read zero.
 */
export function TrackedLedger({
  rows,
}: {
  readonly rows: readonly LedgerRowView[];
}) {
  const decided = rows.filter(
    (row) => row.result === "WIN" || row.result === "LOSS",
  );
  const wins = decided.filter((row) => row.result === "WIN").length;
  const clv = rows
    .map((row) => row.priceClv)
    .filter((value): value is number => value !== null);
  const avgClv =
    clv.length === 0 ? null : clv.reduce((a, b) => a + b, 0) / clv.length;
  const sorted = [...rows].sort((a, b) =>
    resolvedAt(b).localeCompare(resolvedAt(a)),
  );
  return (
    <section className="card tracked-ledger" aria-label="Tracked rules">
      <div className="section-heading">
        <div>
          <span className="eyebrow">No stake · not in the ledger above</span>
          <h3>Tracked rules</h3>
        </div>
        <span>
          {rows.length} tracked ·{" "}
          {decided.length === 0
            ? "–"
            : `${((wins / decided.length) * 100).toFixed(1)}% strike`}{" "}
          ·{" "}
          {avgClv === null
            ? "no CLV yet"
            : `avg price CLV ${signedFixed(avgClv, 3)} (${String(clv.length)}), beat close ${String(clv.filter((v) => v > 0).length)}`}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="tile-empty">No tracked bets yet.</p>
      ) : (
        <div className="table-shell">
          <table>
            <thead>
              <tr>
                <th>Game</th>
                <th>Rule</th>
                <th>Bet</th>
                <th className="numeric">Price</th>
                <th className="numeric">Close</th>
                <th className="numeric">Price CLV</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.betId}>
                  <td>
                    {row.gameLabel}
                    <br />
                    <small>
                      {melbourneStamp(row.gameStartsAt ?? row.placedAt)}
                    </small>
                  </td>
                  <td>{row.ruleVersion}</td>
                  <td>{row.description}</td>
                  <td className="numeric tabular">
                    {row.actualLine === null
                      ? ""
                      : `${decimal(row.actualLine)} @ `}
                    {row.actualPrice.toFixed(2)}
                  </td>
                  <td className="numeric tabular">
                    {row.closingPrice === null
                      ? "–"
                      : `${row.closingLine === null ? "" : `${decimal(row.closingLine)} @ `}${row.closingPrice.toFixed(2)}`}
                  </td>
                  <td className="numeric tabular">
                    {row.priceClv === null ? "–" : signedFixed(row.priceClv, 3)}
                  </td>
                  <td>{row.result ?? "Open"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function LedgerPage({
  rows = [],
  trackedRows = [],
  exportUrl = "/v1/exports/placed.csv",
  prototype = false,
  onSettle,
  onExport,
  onAmend,
}: {
  rows?: readonly RawLedgerRow[];
  /** TEST_ONLY / TRACK_ONLY rows, shown apart and never counted above. */
  trackedRows?: readonly RawLedgerRow[];
  exportUrl?: string;
  prototype?: boolean;
  onSettle?: (betId: string) => void;
  onExport?: () => void;
  /** Correct the book or price on a bet that is already logged. */
  onAmend?: (input: {
    sport: string;
    betId: string;
    bookmaker?: string;
    actualPrice?: number;
  }) => Promise<void>;
}) {
  const [amending, setAmending] = useState<string | null>(null);
  // Recomputed per render rather than memoised: the awaiting-result age is a
  // live number, and the page refetches every minute.
  const now = new Date();
  const chart = useChartState();
  // Filtering by outcome, because the question asked of this page is almost
  // always about one outcome at a time: what lost, what is still open, what
  // was voided and needs chasing at the book.
  const [outcome, setOutcome] = useState<
    "ALL" | "WIN" | "LOSS" | "PUSH" | "VOID" | "OPEN"
  >("ALL");
  // The API already splits them; filtered again here so a tracked row can
  // never reach the official counts even from an older API.
  const normalized = normalizeLiveLedger(rows).filter(
    (row) => row.executionMode !== "TEST_ONLY",
  );
  const tracked = [
    ...normalizeLiveLedger(trackedRows),
    ...normalizeLiveLedger(rows).filter(
      (row) => row.executionMode === "TEST_ONLY",
    ),
  ];
  const liveRows = normalized.filter(
    (row) => row.result === null && row.liveProgress?.status === "IN_PROGRESS",
  );
  // Book filter and sort (Moss, 30 Oct 2026). Rows arrive newest first.
  const [book, setBook] = useState("ALL");
  const [sortBy, setSortBy] = useState<LedgerSort>("newest");
  // Search (Moss, 3 Nov 2026): every word must appear somewhere on the bet,
  // so "herro 3pm" finds his threes bets.
  const [query, setQuery] = useState("");
  // Sport and rule filters (Moss, 3 Nov 2026), beside the book filter.
  const [sportFilter, setSportFilter] = useState("ALL");
  const [rule, setRule] = useState("ALL");
  const visible = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const kept = normalized.filter(
      (row) =>
        (outcome === "ALL" ||
          (outcome === "OPEN"
            ? row.result === null
            : row.result === outcome)) &&
        (book === "ALL" || row.bookmaker === book) &&
        (sportFilter === "ALL" || row.sport === sportFilter) &&
        (rule === "ALL" || row.ruleVersion === rule) &&
        words.every((word) =>
          [
            row.description,
            row.ruleVersion,
            row.bookmaker,
            row.gameLabel,
            row.matchup ?? "",
            row.resultReason ?? "",
          ]
            .join(" ")
            .toLowerCase()
            .includes(word),
        ),
    );
    const time = (iso: string | null | undefined) =>
      iso == null ? Number.POSITIVE_INFINITY : Date.parse(iso);
    const by: Record<
      LedgerSort,
      ((a: (typeof kept)[number], b: (typeof kept)[number]) => number) | null
    > = {
      newest: null,
      oldest: () => 0,
      match: (a, b) => time(a.gameStartsAt) - time(b.gameStartsAt),
      book: (a, b) => a.bookmaker.localeCompare(b.bookmaker),
      stake: (a, b) => b.stakeCents - a.stakeCents,
      price: (a, b) => b.actualPrice - a.actualPrice,
      profit: (a, b) =>
        (b.profitUnits ?? -Infinity) - (a.profitUnits ?? -Infinity),
    };
    const compare = by[sortBy];
    if (compare === null) return kept;
    const base = sortBy === "oldest" ? [...kept].reverse() : kept;
    // Stable: ties keep newest first.
    return base
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compare(a.row, b.row) || a.index - b.index)
      .map(({ row }) => row);
  }, [normalized, outcome, book, sortBy, query, sportFilter, rule]);
  const optionsOf = (pick: (row: (typeof normalized)[number]) => string) =>
    [...new Set(normalized.map(pick))].filter(Boolean).sort();
  const bookOptions = [...new Set(normalized.map((row) => row.bookmaker))]
    .map((name) => ({
      name,
      count: normalized.filter((row) => row.bookmaker === name).length,
    }))
    .sort((a, b) => b.count - a.count);
  const cumulative = cumulativeUnits(normalized);
  const settledRows = normalized.filter((row) => row.result !== null);
  const profitUnits = settledRows.reduce(
    (total, row) => total + (row.profitUnits ?? 0),
    0,
  );
  // The count that answers "is anything stuck?" without reading the table.
  const awaiting = normalized.filter(
    (row) =>
      placedBetProgress(
        {
          status: row.status,
          result: row.result,
          gameStartsAt: row.gameStartsAt ?? null,
        },
        now,
      ).phase === "AWAITING_RESULT",
  ).length;
  const turnoverCents = normalized.reduce(
    (total, row) => total + row.stakeCents,
    0,
  );
  return (
    <div className="board-grid">
      <KpiStrip columns={4}>
        <Figure
          label="Placed"
          value={String(normalized.length)}
          sub={`${liveRows.length} running`}
        />
        <Figure
          label="Settled"
          value={String(settledRows.length)}
          sub={
            awaiting > 0
              ? `${String(awaiting)} awaiting result`
              : `${String(normalized.length - settledRows.length)} open`
          }
        />
        <Figure
          label="Profit"
          value={`${profitUnits >= 0 ? "+" : ""}${profitUnits.toFixed(2)}u`}
          tone={profitUnits >= 0 ? "positive" : "negative"}
        />
        <Figure
          label="Turnover"
          value={`A$${(turnoverCents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub="Staked across every bet"
        />
      </KpiStrip>
      <div className="board-wide board-actions">
        {prototype || onExport !== undefined ? (
          <button className="button primary" type="button" onClick={onExport}>
            {prototype ? "Export prototype CSV" : "Export placed CSV"}
          </button>
        ) : (
          <a className="button primary" href={exportUrl}>
            Export placed CSV
          </a>
        )}
      </div>
      {liveRows.length > 0 ? (
        <section
          className="card live-bet-tracker"
          aria-label="Live bet tracker"
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">In-game monitoring</span>
              <h3>Live bet tracker</h3>
            </div>
            <span>
              {liveRows.length} live {liveRows.length === 1 ? "bet" : "bets"}
            </span>
          </div>
          <div className="live-bet-grid">
            {liveRows.map((row) => (
              <article className="live-bet-item" key={`live-${row.betId}`}>
                <div>
                  <span className="eyebrow">{row.ruleVersion}</span>
                  {/* The bet itself, then the game it is in. An NHL row has
                      no subject name, so this read "Game" on every card. */}
                  <h4>{row.description}</h4>
                  <small>
                    {row.matchup ?? row.gameLabel} · {row.bookmaker} @{" "}
                    {row.actualPrice.toFixed(2)}
                  </small>
                  {row.liveNote ? (
                    <p className="live-note">{row.liveNote}</p>
                  ) : null}
                </div>
                <div className="live-bet-reading">
                  <span>Current / bet</span>
                  <strong>
                    {decimal(row.liveProgress?.actualValue ?? null)} /{" "}
                    {row.selection ? `${sentenceCase(row.selection)} ` : ""}
                    {row.actualLine === null ? "H2H" : decimal(row.actualLine)}
                  </strong>
                  {/* The denominator, where the market has an honest one. A
                      low-count prop does not decay like a points line, so how
                      much opportunity has been spent says more than the count
                      on its own. */}
                  {row.liveProgress?.opportunityValue === null ||
                  row.liveProgress?.opportunityValue === undefined ? null : (
                    <small>
                      from {decimal(row.liveProgress.opportunityValue)}{" "}
                      {row.liveProgress.opportunityLabel ?? ""}
                    </small>
                  )}
                </div>
                <div className="live-bet-reading">
                  <span>Score</span>
                  <strong>
                    {decimal(row.liveProgress?.awayScore ?? null)}–
                    {decimal(row.liveProgress?.homeScore ?? null)}
                  </strong>
                  <small>
                    Updated {melbourneClock(row.liveProgress?.observedAt ?? "")}
                  </small>
                </div>
                {clockLabel({
                  sport: row.sport ?? "NFL",
                  period: row.liveProgress?.period ?? null,
                  clockSeconds: row.liveProgress?.clockSeconds ?? null,
                }) === null ? null : (
                  <div className="live-bet-reading">
                    <span>Clock</span>
                    <strong>
                      {clockLabel({
                        sport: row.sport ?? "NFL",
                        period: row.liveProgress?.period ?? null,
                        clockSeconds: row.liveProgress?.clockSeconds ?? null,
                      })}
                    </strong>
                    <small>
                      {Math.round(
                        (fractionElapsed({
                          sport: row.sport ?? "NFL",
                          period: row.liveProgress?.period ?? null,
                          clockSeconds: row.liveProgress?.clockSeconds ?? null,
                        }) ?? 0) * 100,
                      )}
                      % played
                    </small>
                  </div>
                )}
                {(() => {
                  // Where the current rate lands by full time. Coloured
                  // against the side that was backed, so the panel can be read
                  // at a glance without working out which way the bet runs.
                  const clock = {
                    sport: row.sport ?? "NFL",
                    period: row.liveProgress?.period ?? null,
                    clockSeconds: row.liveProgress?.clockSeconds ?? null,
                  };
                  const projected = projectedFinal(
                    clock,
                    row.liveProgress?.actualValue ?? null,
                  );
                  // The room is exact arithmetic and worth showing from the
                  // first point; the pace needs enough game behind it first.
                  const room = roomToLine(
                    row.liveProgress?.actualValue ?? null,
                    row.actualLine,
                    row.selection ?? "",
                  );
                  if (projected === null && room === null) return null;
                  // Coloured by where the bet stands if the game ended now,
                  // which is exact, rather than by the projection, which a
                  // measurement of 379 games says lands within three points
                  // only a fifth of the time.
                  const winning =
                    room === null
                      ? projectionBeatsLine(
                          projected,
                          row.actualLine,
                          row.selection ?? "",
                        )
                      : room.phrase === "to spare" ||
                        room.phrase === "past the line";
                  return (
                    <div className="live-bet-reading">
                      <span>{room === null ? "On track" : "Room"}</span>
                      <strong
                        className={
                          winning === null
                            ? undefined
                            : winning
                              ? "is-positive"
                              : "is-negative"
                        }
                      >
                        {room === null ? projected : decimal(room.points)}
                      </strong>
                      <small>
                        {room === null
                          ? "at this rate"
                          : `${room.phrase} · ${projected} at this pace`}
                      </small>
                    </div>
                  );
                })()}
                {inningLabel(row.liveProgress ?? null) === null ? null : (
                  <div className="live-bet-reading">
                    <span>Inning</span>
                    <strong>{inningLabel(row.liveProgress ?? null)}</strong>
                    <small>
                      {row.liveProgress?.status === "FINAL"
                        ? "final"
                        : `${row.liveProgress?.inningHalf === "TOP" ? "top" : "bottom"}${
                            row.liveProgress?.outs === null ||
                            row.liveProgress?.outs === undefined
                              ? ""
                              : ` · ${String(row.liveProgress.outs)} out`
                          }`}
                    </small>
                  </div>
                )}
                {lineupLabel(row.liveProgress ?? null) === null ? null : (
                  <div className="live-bet-reading">
                    <span>Lineup</span>
                    <strong>{lineupLabel(row.liveProgress ?? null)}</strong>
                    <small>
                      {row.liveProgress?.playerRemoved === true
                        ? "number is final"
                        : row.liveProgress?.teamBatting
                          ? "batting"
                          : "fielding"}
                    </small>
                  </div>
                )}
                {row.liveProgress?.expectedResult === "WIN" ||
                row.liveProgress?.expectedResult === "LOSS" ? (
                  <div className="live-bet-reading">
                    <span>Expected</span>
                    <strong
                      className={
                        row.liveProgress.expectedResult === "WIN"
                          ? "is-positive"
                          : "is-negative"
                      }
                    >
                      {row.liveProgress.expectedResult === "WIN"
                        ? "Win"
                        : "Loss"}
                    </strong>
                    <small>settles at full time</small>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
      <section className="card ledger-card">
        <div className="section-heading">
          <h3>Cumulative units</h3>
          <ChartControls state={chart} />
          <span>Sparse closing snapshots</span>
        </div>
        <UnitsChart
          data={
            cumulative.length === 0
              ? [{ day: "No bets", units: 0 }]
              : cumulative
          }
          state={chart}
        />
      </section>
      <section className="ledger-records" aria-label="Bet lifecycle records">
        <div className="section-heading">
          <h3>Bet lifecycle</h3>
          <div
            className="outcome-filter"
            role="group"
            aria-label="Filter by outcome"
          >
            {OUTCOMES.map((option) => {
              const count = countFor(normalized, option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={outcome === option.id}
                  className={
                    option.tone === undefined ? "" : `is-${option.tone}`
                  }
                  onClick={() => setOutcome(option.id)}
                  disabled={count === 0 && option.id !== "ALL"}
                >
                  {option.label}
                  <span className="tabular">{count}</span>
                </button>
              );
            })}
          </div>
          <span>
            {visible.length} of {normalized.length}
          </span>
        </div>
        <TableToolbar
          book={book}
          books={bookOptions}
          onBook={setBook}
          sort={sortBy}
          sorts={LEDGER_SORTS}
          onSort={setSortBy}
        >
          <label>
            <span>Find</span>
            <input
              type="search"
              value={query}
              placeholder="Player, rule, book"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {optionsOf((row) => row.sport ?? "").length > 1 ? (
            <label>
              <span>Sport</span>
              <select
                value={sportFilter}
                onChange={(event) => setSportFilter(event.target.value)}
              >
                <option value="ALL">All sports</option>
                {optionsOf((row) => row.sport ?? "").map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            <span>Rule</span>
            <select
              value={rule}
              onChange={(event) => setRule(event.target.value)}
            >
              <option value="ALL">All rules</option>
              {optionsOf((row) => row.ruleVersion).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </TableToolbar>
        {visible.length === 0 ? (
          <p className="tile-empty">Nothing matches those filters yet.</p>
        ) : null}
        {visible.map((row) => (
          <article className="ledger-row card" key={row.betId}>
            <div className="ledger-row-heading">
              <div>
                <span className="eyebrow">{row.ruleVersion}</span>
                <h4>{row.description}</h4>
                <small>
                  {row.gameLabel} · {row.bookmaker}
                </small>
                {/* Match day and bet day side by side: a bet struck four
                    days before kickoff is a different bet from one struck
                    an hour out. */}
                <small>
                  {row.gameStartsAt
                    ? `Match ${melbourneStamp(row.gameStartsAt)} · `
                    : ""}
                  Bet {melbourneStamp(row.placedAt)}
                </small>
                <div className="ledger-badges">
                  {/* The system retracted this after alerting it. Without this
                      the operator can place a bet it no longer wants. */}
                  {row.alertStatus === "WITHDRAWN" ||
                  row.alertStatus === "WITHDRAWN_PENDING" ? (
                    <span className="badge warn">Alert withdrawn</span>
                  ) : null}
                  {/* Graded from the backup feed, whose box score lags its own
                      final flag - two values were wrong on 12 September. */}
                  {row.settlementSource === "highlightly" ? (
                    <span className="badge">Graded from backup</span>
                  ) : null}
                  {row.loggedAfterTheFact ? (
                    <span className="badge">Logged after the fact</span>
                  ) : null}
                </div>
              </div>
              <span
                className={`ledger-status ${row.result?.toLowerCase() ?? "open"}`}
              >
                {row.result === "VOID"
                  ? "Void · stake returned"
                  : (row.result ??
                    describePlacedBetPhase(
                      placedBetProgress(
                        {
                          status: row.status,
                          result: row.result,
                          gameStartsAt: row.gameStartsAt ?? null,
                        },
                        now,
                      ),
                    ))}
              </span>
            </div>
            {row.resultReason ? (
              <p className="ledger-reason">{row.resultReason}</p>
            ) : null}
            <div className="ledger-facts">
              <div>
                <span>Placed</span>
                <strong>
                  {row.actualLine === null
                    ? "H2H"
                    : `Line ${decimal(row.actualLine)}`}{" "}
                  @ {row.actualPrice.toFixed(2)}
                </strong>
              </div>
              {row.actualValue == null || row.result === null ? null : (
                <div>
                  <span>Line vs result</span>
                  <strong>
                    {row.selection ? `${sentenceCase(row.selection)} ` : ""}
                    {row.actualLine === null
                      ? ""
                      : decimal(row.actualLine)} → {decimal(row.actualValue)}
                  </strong>
                </div>
              )}
              <div>
                <span>Stake</span>
                <strong>
                  {row.units}u · {money(row.stakeCents)}
                </strong>
              </div>
              <div>
                <span>Result</span>
                <strong>
                  {row.profitUnits === null
                    ? "Awaiting settlement"
                    : `${row.profitUnits >= 0 ? "+" : ""}${row.profitUnits.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}u`}
                </strong>
                {row.result === null && row.liveProgress ? (
                  <small>
                    Live value {decimal(row.liveProgress.actualValue)} · Score{" "}
                    {decimal(row.liveProgress.awayScore)}–
                    {decimal(row.liveProgress.homeScore)} · updated{" "}
                    {melbourneClock(row.liveProgress.observedAt)}
                  </small>
                ) : null}
              </div>
              <div>
                <span>
                  {row.pretipSnapshotStatus == null
                    ? "Sparse close / CLV"
                    : "Last pre-tip price / movement"}
                </span>
                {row.pretipSnapshotStatus != null ? (
                  <>
                    <strong>
                      {row.pretipSnapshotStatus === "UNAVAILABLE"
                        ? "Unavailable"
                        : row.pretipSnapshotStatus === "LINE_MOVED"
                          ? `Line moved to ${decimal(row.lastPretipLine ?? null)}`
                          : `Exact ${decimal(row.lastPretipLine ?? null)} @ ${(row.lastPretipPrice ?? 0).toFixed(2)}`}
                    </strong>
                    <small>
                      {row.pretipSnapshotStatus === "EXACT"
                        ? `Price move ${signedFixed(row.pretipPriceMovement ?? 0, 2)}`
                        : row.pretipSnapshotStatus === "LINE_MOVED"
                          ? // Prices at different lines are not comparable, so
                            // only the line move is reported.
                            `Line move ${signedFixed(row.pretipLineMovement ?? 0, 1)} · price not comparable`
                          : "No eligible snapshot before this game started"}
                      {row.lastPretipSnapshotAt == null
                        ? ""
                        : ` · seen ${melbourneClock(row.lastPretipSnapshotAt)}`}
                      {row.lastPretipSourceAt == null
                        ? ""
                        : ` · book updated ${melbourneClock(row.lastPretipSourceAt)}`}
                    </small>
                  </>
                ) : (
                  <>
                    <strong>
                      {row.closingPrice === null
                        ? row.status === "PLACED"
                          ? "Snapshot pending"
                          : row.fallbackSnapshotPrice !== null &&
                              row.fallbackSnapshotPrice !== undefined
                            ? `Last saved ${row.fallbackSnapshotWindow ?? "pre-game"} ${decimal(row.fallbackSnapshotLine ?? null)} @ ${row.fallbackSnapshotPrice.toFixed(2)}`
                            : "No matching quote captured"
                        : `Closing ${decimal(row.closingLine)} @ ${row.closingPrice.toFixed(2)}`}
                    </strong>
                    {row.closingPrice === null &&
                    row.status !== "PLACED" &&
                    row.fallbackSnapshotPrice !== null &&
                    row.fallbackSnapshotPrice !== undefined ? (
                      <small>
                        Pre-close price move{" "}
                        {signedFixed(
                          row.actualPrice - row.fallbackSnapshotPrice,
                          2,
                        )}{" "}
                        · not CLV
                      </small>
                    ) : row.closingPrice === null ? null : (
                      <small>
                        Line CLV {decimal(row.lineClv)} · Price CLV{" "}
                        {decimal(row.priceClv)}
                      </small>
                    )}
                  </>
                )}
              </div>
            </div>
            {onAmend === undefined || prototype ? null : (
              <button
                className="button secondary"
                type="button"
                onClick={() => setAmending(row.betId)}
              >
                Correct book or price
              </button>
            )}
            {amending !== row.betId || onAmend === undefined ? null : (
              <AmendBetDialog
                bet={{
                  betId: row.betId,
                  sport: row.sport ?? "NFL",
                  description: row.description,
                  bookmaker: row.bookmaker,
                  actualPrice: row.actualPrice,
                }}
                onCancel={() => setAmending(null)}
                onAmend={async (input) => {
                  await onAmend(input);
                  setAmending(null);
                }}
              />
            )}
            {prototype && row.status === "PLACED" ? (
              <button
                className="button secondary"
                type="button"
                onClick={() => onSettle?.(row.betId)}
              >
                Simulate close &amp; settle
              </button>
            ) : null}
          </article>
        ))}
      </section>
      <TrackedLedger rows={tracked} />
    </div>
  );
}
